import Dexie, { Table } from 'dexie'
import store from '@/store'

const getDefaultMessage = (id: number = 0, hId: number = 0, type: string = 'welcome') => {
    return {
        id,
        hId,
        type,
        createTime: Date.now(),
        from: 'receive' as 'receive',
        status: 'success' as 'success',
        userId: store.getters['user/userId']
    }
}

// export interface Image {
//     id: number // 图片ID
//     url: string // 原始图片URL
//     clipId: string // 裁剪图片ID
//     clipUrl: string // 裁剪图片URL
// }

// 定义聊天消息类型
export interface ChatMessage {
    id: number // 消息ID
    hId: number
    createTime: number // 创建时间
    from: 'send' | 'receive' // 消息来源
    type: 'text' | 'image' | 'reAnswer' | 'recommend' | 'quizMe' | 'welcome' | 'welcomeForClear' // 消息类型
    status?: 'pending' | 'thinking' | 'receiving' | 'success' | 'error' | 'canceled'
    image?: string // 图片
    imageId?: number // 图片ID
    content?: string // 消息内容
    relativeId?: number // 关联原发送ID
    relativeHId?: number // 关联原发送历史ID
    errorData?: {
        errorMessage: string // 错误信息
    }
    recommend?: boolean // 是否是推荐问题
    like?: number // 点赞状态
    sessionId?: number // 服务端关联会话ID
    sessionMessageId?: number // 服务端关联消息ID
    userId: number | string // 用户ID，
    generateImage?: {
        need: boolean
        status: 'waiting' | 'success' | 'failed'
        filePath?: string
    }
}

// 会话历史
export interface StudyChatHistory {
    id?: number // 历史ID
    studySetId: string // 关联的学习集ID（用于按学习集拉取聊天记录）
    title: string // 会话标题
    createTime: number // 创建时间
    userId: number | string // 用户ID
    messageList: ChatMessage[] // 消息列表
}

// 创建历史记录时使用的类型（不包含自增id）
export type CreateStudyChatHistory = Omit<StudyChatHistory, 'id'>

// 数据库类
export class ChatDatabase extends Dexie {
    // 声明表
    StudyChatHistory!: Table<StudyChatHistory, number>

    constructor() {
        // IMPORTANT: do NOT share DB name with global chat module
        super('StudyChatDatabase')

        // 定义数据库结构
        this.version(1).stores({
            // 历史记录表：按id、userId和创建时间索引
            StudyChatHistory: '++id, userId, createTime, title'
        })

        // v2: add studySetId for per-study-set chat history
        this.version(2)
            .stores({
                // add indexes and compounds for efficient lookup
                StudyChatHistory:
                    '++id, userId, studySetId, createTime, title, [userId+studySetId], [userId+studySetId+createTime]'
            })
            .upgrade(async (tx) => {
                // Backfill for existing records (no studySetId before)
                const table = tx.table('StudyChatHistory')
                await table.toCollection().modify((h: any) => {
                    if (h.studySetId == null) h.studySetId = ''
                })
            })

        // 添加钩子函数
        // @ts-ignore
        this.StudyChatHistory.hook('creating', (primKey: any, obj: any, trans: any) => {
            obj.createTime = obj.createTime || Date.now()
            obj.userId = store.getters['user/userId']
        })

        // 暂时保留
        // this.StudyChatHistory.hook('updating', (modifications: any, primKey: any, obj: any, trans: any) => {
        // })
    }

    // 初始化数据库
    async initDatabase() {
        try {
            await this.open()

            // 检查是否需要初始化默认数据
            await this.initDefaultData()

            return true
        } catch (error) {
            console.error('Init database failed:', error)
            return false
        }
    }

    // 初始化默认数据
    private async initDefaultData() {
        // 检查是否已有历史记录
        const historyList = await dbUtils.getUserHistories()
        const lastIsOnlyWelcome = historyList.length > 0 ? await this.lastIsOnlyWelcome(historyList) : false

        if (!historyList.length || !lastIsOnlyWelcome) {
            await dbUtils.createHistory('welcome')
        }
    }

    private async lastIsOnlyWelcome(historyList: StudyChatHistory[]) {
        var lastHistory = historyList[historyList.length - 1]
        var messageList = lastHistory.messageList

        return messageList.length == 1 && messageList[0].type == 'welcome'
    }
}

// 导出数据库实例
export const studyChatDB = new ChatDatabase()

// 数据库工具函数
export const dbUtils = {
    // 获取用户的所有历史记录
    async getUserHistories(param?: { type?: string; studySetId?: string }) {
        let result: StudyChatHistory[] = []

        const userId = store.getters['user/userId']
        const studySetId = param?.studySetId

        // When studySetId is provided, only load histories for that study set
        if (studySetId != null) {
            if (param?.type == 'init') {
            result = await studyChatDB.StudyChatHistory.where('[userId+studySetId]')
                    .equals([userId, studySetId])
                    .reverse()
                    .limit(1)
                    .toArray()
            } else {
                result = await studyChatDB.StudyChatHistory.where('[userId+studySetId]').equals([userId, studySetId]).toArray()
            }
        } else {
            // Fallback (legacy): user scoped only
            if (param?.type == 'init') {
                result = await studyChatDB.StudyChatHistory.where('userId').equals(userId).reverse().limit(1).toArray()
            } else {
                result = await studyChatDB.StudyChatHistory.where('userId').equals(userId).toArray()
            }
        }

        // 手动按创建时间排序（降序）
        const sorted = result.sort((a, b) => a.createTime - b.createTime)

        return sorted
    },

    // 获取特定历史记录
    async getHistoryById(historyId: number) {
        return await studyChatDB.StudyChatHistory.get(historyId)
    },

    // 创建新的历史记录
    async createHistory(title: string = 'New history', type: string = 'welcome', studySetId: string = '') {
        const history: CreateStudyChatHistory = {
            studySetId,
            title,
            createTime: Date.now(),
            userId: store.getters['user/userId'],
            messageList: []
        }

        let hId = await studyChatDB.StudyChatHistory.add(history as any),
            defaultMessageList: ChatMessage

        if (type == 'welcome' || type == 'welcomeForClear' || type == 'newRound') {
            // @ts-ignore
            defaultMessageList = getDefaultMessage(0, hId, type)

            await dbUtils.addMessageToHistory(hId, defaultMessageList)
        }
        // @ts-ignore
        return { hId, history: { ...history, messageList: defaultMessageList ? [defaultMessageList] : [] } }
    },

    // 添加消息到历史记录
    async addMessageToHistory(historyId: number, message: ChatMessage) {
        const history = await studyChatDB.StudyChatHistory.get(historyId)

        if (!history) {
            throw new Error('History not found')
        }

        const messageId = history.messageList.length || 0

        message.id = messageId

        // 更新历史记录的消息列表
        const updatedMessageList = [...history.messageList, message]

        await studyChatDB.StudyChatHistory.update(historyId, {
            messageList: updatedMessageList
        })

        return messageId
    },

    // 获取历史记录中的特定消息
    async getMessageFromHistory(historyId: number, messageId: number) {
        const history = await studyChatDB.StudyChatHistory.get(historyId)

        return history?.messageList.find(msg => msg.id === messageId) || null
    },

    // 更新历史记录中的消息
    async updateMessageInHistory(historyId: number, messageId: number, updates: Partial<ChatMessage>) {
        const history = await studyChatDB.StudyChatHistory.get(historyId)

        if (!history) {
            throw new Error('History not found')
        }

        const messageIndex = history.messageList.findIndex(msg => msg.id === messageId)

        if (messageIndex === -1) {
            throw new Error('Message not found')
        }

        // 更新消息
        const updatedMessage = { ...history.messageList[messageIndex], ...updates }
        const updatedMessageList = [...history.messageList]
        updatedMessageList[messageIndex] = updatedMessage

        await studyChatDB.StudyChatHistory.update(historyId, {
            messageList: updatedMessageList
        })

        return updatedMessage
    },

    // 删除历史记录中的消息
    async deleteMessageFromHistory(historyId: number, messageId: number) {
        const history = await studyChatDB.StudyChatHistory.get(historyId)

        if (!history) {
            throw new Error('History not found')
        }

        const updatedMessageList = history.messageList.filter(msg => msg.id !== messageId)

        await studyChatDB.StudyChatHistory.update(historyId, {
            messageList: updatedMessageList
        })
    },

    // 获取历史记录列表
    async getSnapHistoryList(pageInfo: any) {
        const { currentPage = 1, pageSize = 9 } = pageInfo
        const offset = (currentPage - 1) * pageSize

        // 获取所有用户数据并过滤出包含send消息的记录
        const allUserData = await studyChatDB.StudyChatHistory.where('userId').equals(store.getters['user/userId']).toArray()

        // 过滤出包含messageList且messageList中包含from='send'的记录
        const filteredData = allUserData.filter(history => {
            return history.messageList && history.messageList.length > 0 && history.messageList.some(message => message.from === 'send')
        })

        // 按创建时间倒序排列
        const sortedData = filteredData.sort((a, b) => b.createTime - a.createTime)

        // 获取过滤后的总数
        const total = sortedData.length

        // 手动分页
        const result = sortedData.slice(offset, offset + pageSize)

        return {
            data: result,
            total,
            currentPage,
            pageSize,
            totalPages: Math.ceil(total / pageSize)
        }
    }
}
